import React, { FC } from "react";
import { useAuth } from "../../../../context/user.context";
import {
  Container,
  Pie,
  DiagramWrapper,
  DiagramName,
  Description,
  PieBorder,
  DiagramContent,
} from "./styles";
import {formatQuotasRam, formatQuotasStorage} from '../../../../utils/sizes'
import { useTranslation } from "react-i18next";
import Skeleton from 'react-loading-skeleton'
import styled, { useTheme } from "styled-components";
import { permissionSurfaces, usePermissionDecision } from '../../../../permissions';
import { useAnimatedPercentage } from '../../../../hooks/useAnimatedPercentage';

type PieProp = {
  item:any
}
const SkeletonWrapper = styled(Skeleton)`
  width: 82px;
  min-width: 82px;
  height: 82px;
  min-height: 82px;
  flex: 0 0 82px;
  border-radius:100%;
  margin-bottom:20px;

  @media screen and (max-width: 768px) {
    width: 42px;
    min-width: 42px;
    height: 42px;
    min-height: 42px;
    margin-bottom: 0;
    flex-basis: 42px;
  }
`;

const SkeletonContent = styled(DiagramContent)`
  width: 135px;
  gap: 12px;

  @media screen and (max-width: 768px) {
    width: auto;
    flex: 1;
    gap: 4px;
  }
`

const Diagrams:FC = () => {
  const {quotas, quotasLoading} = useAuth()
  const [t] = useTranslation()
  const theme:any = useTheme()
  const canViewSummary = usePermissionDecision(permissionSurfaces.detailActions.summaryView).allowed
  if (!canViewSummary) {
    return null
  }
  const data = [
    {
      title:t("INSTANCES"),
      desc:`${t("USED")} ${quotas?.core?.instances?.in_use} ${t("OF")} ${quotas?.core?.instances?.limit}`,
      id:'1',
      total:quotas?.core?.instances?.limit,
      used:quotas?.core?.instances?.in_use
    },
    {
      title:t("VCPUS"),
      desc:`${t("USED")} ${quotas?.core?.cores?.in_use} ${t("OF")} ${quotas?.core?.cores?.limit}`,
      id:'2',
      total:quotas?.core?.cores?.limit,
      used:quotas?.core?.cores?.in_use
    },
    {
      title:t("RAM"),
      desc:`${t("USED")} ${formatQuotasRam(quotas?.core?.ram?.in_use)} ${t("OF")} ${formatQuotasRam(quotas?.core?.ram?.limit)}`,
      id:'3',
      total:quotas?.core?.ram?.limit,
      used:quotas?.core?.ram?.in_use
    },
    {
      title:t("STORAGE"),
      desc:`${t("USED")} ${formatQuotasStorage(quotas?.volume?.gigabytes?.in_use)} ${t("OF")} ${formatQuotasStorage(quotas?.volume?.gigabytes?.limit)}`,
      id:'4',
      total:quotas?.volume?.gigabytes?.limit,
      used:quotas?.volume?.gigabytes?.in_use
    },
    {
      title:t("VOLUMES"),
      desc:`${t("USED")} ${quotas?.volume?.volumes?.in_use} ${t("OF")} ${quotas?.volume?.volumes?.limit}`,
      id:'5',
      total:quotas?.volume?.volumes?.limit,
      used:quotas?.volume?.volumes?.in_use
    },
    {
      title:t("IP"),
      desc:`${t("USED")} ${quotas?.network?.floatingip?.used} ${t("OF")} ${quotas?.network?.floatingip?.limit}`,
      id:'6',
      total:quotas?.network?.floatingip?.limit,
      used:quotas?.network?.floatingip?.used
    }
  ]
  if(quotasLoading) return (
    <Container data-test-id='quotas-summary-container'>
      {[1,2,3,4,5,6].map((item) => (
        <DiagramWrapper key={item}>
          <SkeletonWrapper
            baseColor={theme.palette[300]}
            highlightColor={theme.palette[20]}
          />
          <SkeletonContent>
            <Skeleton
              baseColor={theme.palette[300]}
              highlightColor={theme.palette[20]}
              width="100%" height={15}
            />
            <Skeleton
              baseColor={theme.palette[300]}
              highlightColor={theme.palette[20]}
              width="100%" height={15}
            />
          </SkeletonContent>
        </DiagramWrapper>
      ))}
    </Container>
  )
  if(quotas?.length === 0) return null
  return (
    <Container data-test-id='quotas-summary-container'>
      {data.map((item:any) => (
        <PieComponent key={item.id} item={item}/>
      ))}
    </Container>
  )
}


export default Diagrams

// import React, { FC, useEffect, useState } from "react";
// import { useTheme } from "styled-components";
// import {
//   Container,
//   Pie,
//   DiagramWrapper,
//   DiagramName,
//   Description,
//   PieBorder,
// } from "./styles";

// type PieProp = {
//   item: {
//     id: string;
//     title: string;
//     desc: string;
//     total: number;
//     used: number;
//   };
// };

const PieComponent: FC<PieProp> = ({ item }) => {
  const rawComputed = item.total === 0 ? 0 : Math.round((item.used / item.total) * 100);
  const computed = Number.isFinite(rawComputed) ? Math.min(100, Math.max(0, rawComputed)) : 0;
  const {value:animateComputed, duration} = useAnimatedPercentage(computed);
  const pieStyle = {
    '--quota-used': `${animateComputed}%`,
    '--quota-rotation': `${3.6 * animateComputed}deg`,
    '--quota-duration': duration,
  } as React.CSSProperties;

  return (
    <DiagramWrapper key={item.id}>
      <Pie data-test-id={item.id === '1' ? 'quota-pie-instances' : undefined} style={pieStyle}>
        {computed > 0 && computed < 100 ? (
          <PieBorder />
        ) : null}
      </Pie>
      <DiagramContent>
        <DiagramName data-test-id={item.id === "1" ? "instance-quota-container" : "other"}>
          {item.title}
        </DiagramName>
        <Description>{item.desc}</Description>
      </DiagramContent>
    </DiagramWrapper>
  );
};

// export default PieComponent;
